import {
  useGenerateBankListReportMutation,
  useGetBatchSalarySheetQuery,
  useUpdateBatchPaymentStatusMutation,
  useUpdatePaymentStatusMutation,
  type BankListRow,
} from '@/app/features/employee/emp-payroll';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import useFilters from '@/hooks/useFilters';
import { CheckCircleIcon, CheckIcon, DownloadIcon } from '@/icons';
import DashboardLayout from '@/layouts/DashboardLayout';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import PayrollPageHeader from '../shared/PayrollPageHeader';
import SearchInput from '../shared/SearchInput';
import {
  PAYMENT_STATUS_CONFIG,
  PAYROLL_PATH,
} from '../shared/payroll.constants';
import { formatBDT } from '../shared/payroll.helpers';

/** Empty bank fields read as "n/a". */
const textOrNa = (val?: string | null) => val ?? 'n/a';

/** Both payment actions share one size so the toolbar lines up. */
const PAY_BTN = '!h-9 !px-4 !rounded-md !text-sm !font-semibold';

/**
 * The bank list of a batch: who gets paid how much, and to which account.
 * Rows are marked paid here; the list itself is exported through Reports.
 */
export default function BatchSalarySheet() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  /** The export is queued; this offers the way to Reports once it is. */
  const [showExported, setShowExported] = useState(false);

  const { page, limit, keyword, handleSearch, handlePagination } = useFilters();

  const { data, isLoading, isFetching } = useGetBatchSalarySheetQuery(
    { id, args: { params: { page, limit, search: keyword } } },
    { skip: !id },
  );

  const [updatePaymentStatus, { isLoading: updating }] =
    useUpdatePaymentStatusMutation();
  const [generateBankList, { isLoading: exporting }] =
    useGenerateBankListReportMutation();

  const handleExport = async () => {
    try {
      await generateBankList({ batchId: id }).unwrap();
      setShowExported(true);
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to export bank list'));
    }
  };

  const [markAllPaid, { isLoading: markingAll }] =
    useUpdateBatchPaymentStatusMutation();
  const [confirmPayAll, setConfirmPayAll] = useState(false);

  /** Every record of the batch in one call, not just the page on screen. */
  const handleMarkAllPaid = async () => {
    try {
      await markAllPaid({ batchId: id, status: 'PAID' }).unwrap();
      toast('success', 'Whole batch marked as paid.');
      setSelectedIds([]);
      setConfirmPayAll(false);
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to update payment'));
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedIds.length) return;
    try {
      await updatePaymentStatus({ ids: selectedIds, status: 'PAID' }).unwrap();
      toast('success', 'Payment completed successfully.');
      setSelectedIds([]);
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to update payment'));
    }
  };

  const columns: TableProps<BankListRow>['columns'] = [
    {
      title: 'Department',
      dataIndex: 'department_name',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Employee ID',
      dataIndex: 'employee_id',
      render: (val: string) => val || 'n/a',
    },
    { title: 'Employee Name', dataIndex: 'employee_name', render: textOrNa },
    {
      title: 'Net Salary',
      dataIndex: 'net_salary',
      render: (val: string) => formatBDT(val || null),
    },
    { title: 'Bank Name', dataIndex: 'bank_name', render: textOrNa },
    { title: 'Account Name', dataIndex: 'account_name', render: textOrNa },
    { title: 'Account No.', dataIndex: 'account_number', render: textOrNa },
    { title: 'Routing No.', dataIndex: 'routing_number', render: textOrNa },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={PAYMENT_STATUS_CONFIG} />
      ),
    },
  ];

  return (
    <>
      <DashboardLayout title="Employee">
        <PayrollPageHeader
          crumbs={[
            { title: 'Payroll & Salary Sheet Generate', path: PAYROLL_PATH },
            { title: 'Batch', path: `${PAYROLL_PATH}/batch/${id}` },
            { title: 'Salary Sheet Generated' },
          ]}
        >
          <SearchInput
            placeholder="Search Employee ID"
            onChange={handleSearch}
          />
          <Button
            size="large"
            loading={exporting}
            onClick={handleExport}
            className="btn-primary-bordered"
            icon={<DownloadIcon size={16} />}
          >
            Export
          </Button>
        </PayrollPageHeader>

        <div className="mt-6">
          <CustomTable<BankListRow>
            page={page}
            limit={limit}
            columns={columns}
            hideRowSelect
            dataSource={data?.data?.data || []}
            onPaginate={handlePagination}
            customTitle="Salary Sheet Generated"
            loading={isLoading || isFetching}
            tableRootClassName="!bg-transparent !border-none !p-0"
            total={data?.data?.meta?.total || 0}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selectedIds,
              onChange: (keys) => setSelectedIds(keys as string[]),
              getCheckboxProps: (row) => ({
                disabled: row?.status === 'PAID',
                className: 'custom-check',
              }),
              columnWidth: 40,
            }}
            filterSection={
              <div className="flex items-center gap-2">
                <Button
                  size="middle"
                  icon={<CheckIcon size={16} />}
                  loading={updating}
                  disabled={!selectedIds.length || updating}
                  onClick={handleMarkPaid}
                  className={`btn-primary-bordered ${PAY_BTN}`}
                >
                  Mark Paid
                  {selectedIds.length ? ` (${selectedIds.length})` : ''}
                </Button>
                <Button
                  size="middle"
                  icon={<CheckCircleIcon size={16} />}
                  disabled={!id || !(data?.data?.meta?.total ?? 0) || markingAll}
                  onClick={() => setConfirmPayAll(true)}
                  className={`btn-primary !text-theme-gray-650 hover:!text-theme-gray-650 ${PAY_BTN}`}
                >
                  Mark All Paid
                </Button>
              </div>
            }
          />
        </div>
      </DashboardLayout>

      <ConfirmationModal
        open={confirmPayAll}
        loading={markingAll}
        handleClose={() => setConfirmPayAll(false)}
        handleSubmit={handleMarkAllPaid}
        title="Mark All Paid"
        subTitle="Every employee in this batch will be marked as paid."
        buttonName="Mark All Paid"
      />

      <ConfirmationModal
        open={showExported}
        handleClose={() => setShowExported(false)}
        wrapperClassName="p-0"
        handleSubmit={() => navigate('/admin/employee/report')}
        title="Bank List Report"
        classNames={{ titleWrapper: 'items-start text-start' }}
        subTitle="Payroll file request successful! Check reports to download."
        buttonName="Go to Report"
      />
    </>
  );
}
